import React, { useContext, useEffect, useState } from "react";
import apiService, { handleError } from "../../Common/ApiCommon/ApiService";
import { PieChart, Pie, Cell, Legend, Tooltip } from "recharts";
import { UserContext } from "../../Layout/UserContext";

const COLORS = {
  win: "#3963CD",
  loss: "#FA742B",
};

const PlayersStats = () => {
  const [topplayers, setTopplayers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [chartData, setChartData] = useState([]);
  const { userData } = useContext(UserContext);
  const userId = userData?.userId;

  // Fetch top 5 players
  const fetchTopPlayers = async () => {
    setLoading(true);
    try {
      const response = await apiService.get(`/vendors/${userId}/top5_players`);
      setTopplayers(response.data);
    } catch (errors) {
      handleError(errors);
    } finally {
      setLoading(false);
    }
  };

  const fetchPlayerStats = async () => {
    setLoading(true);
    try {
      const response = await apiService.get(`/vendors/${userId}/all_players_result`);
      const { total_wins, total_loose } = response.data;

      const data = [
        { name: "Win", value: total_wins, color: COLORS.win },
        { name: "Loss", value: total_loose, color: COLORS.loss },
      ];
      setChartData(data);
    } catch (errors) {
      handleError(errors);
    }
  };

  useEffect(() => {
    fetchTopPlayers();
    fetchPlayerStats();
  }, []);

  return (
    <div className="flex flex-col gap-6  sm:py-6">
      <div className="flex flex-col sm:flex-row gap-6">
        {/* Pie Chart Section */}
        <div className="bg-white p-4 sm:p-6 rounded-2xl shadow-md w-full lg:max-w-md">
          <h2 className="font-bold text-xl sm:text-2xl text-gray-900">
            Player
          </h2>
          <p className="text-gray-500 text-sm mb-4">Win & Loss Overview</p>

          <div className="flex justify-center">
            {chartData.reduce((sum, item) => sum + item.value, 0) === 0 ? (
              <p className="text-center text-gray-500 mt-4">
                No data to display.
              </p>
            ) : (
              <PieChart width={350} height={250}>
                <Pie
                  data={chartData}
                  cx="50%"
                  cy="50%"
                  innerRadius={50}
                  outerRadius={70}
                  dataKey="value"
                  label={({ name, value }) => `${name} - ${value}`}
                >
                  {chartData.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={entry.color} />
                  ))}
                </Pie>
                <Tooltip />
                <Legend
                  verticalAlign="bottom"
                  align="center"
                  iconType="circle"
                />
              </PieChart>
            )}
          </div>
        </div>

        {/* Top players Section */}
        <div className="w-full bg-white p-4 sm:p-6 rounded-xl shadow-lg overflow-x-auto horizontalscroll">
          <h2 className="text-xl font-bold text-gray-800">Top Players</h2>
          <p className="text-gray-500">Top players name with rating</p>

          <div className="flex gap-4 mt-12 overflow-x-auto horizontalscroll">
            {topplayers?.length === 0 && !loading && (
              <p className="text-gray-600">No top players found.</p>
            )}

            {topplayers?.map((player, index) => {
              const winPercentage = player.games
                ? ((player.winner / player.games) * 100).toFixed(1)
                : "0.0";

              return (
                <div
                  key={index}
                  className="bg-[#F4FDF6] p-4 rounded-xl text-center min-w-[8rem]"
                >
                  <img
                    src={`https://ui-avatars.com/api/?name=${encodeURIComponent(
                      player.full_name
                    )}&background=random`}
                    alt={player.full_name}
                    className="w-16 h-16 rounded-full mx-auto mb-6 object-cover"
                  />
                  <p className="text-gray-800 font-medium text-sm sm:text-base">
                    {player.full_name}
                  </p>
                  <p className="text-gray-600 text-xs sm:text-sm mt-2" >
                    Games: {player.games}
                  </p>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
};

export default PlayersStats;